import {
  ApiError,
  type Context,
  isNotBlank,
  type Logger,
  TYPES,
} from '@bizzdesign/sdk-internal';
import { inject, injectable } from 'inversify';
import { fetch, Headers } from 'cross-fetch';

/**
 * Options for uploading a JSON payload to an S3 presigned URL.
 *
 * @remarks
 * - The uploader always uses `PUT`.
 * - The request `content-type` is enforced to `application/json` and cannot be overridden.
 * - By default the uploader sets `x-amz-server-side-encryption: AES256` if it is not provided.
 * - A minimum timeout is enforced to avoid accidental extremely small values.
 */
export type PresignedUrlUploadOptions = {
  /**
   * Request timeout in milliseconds.
   *
   * @remarks
   * If a value lower than the minimum is provided, the minimum timeout is used instead.
   */
  readonly timeoutMs?: number;
  /**
   * Additional headers to include in the upload request.
   *
   * @remarks
   * - `content-type` is always set to `application/json` and will overwrite any provided value.
   * - If `x-amz-server-side-encryption` is not provided, it defaults to `AES256`.
   */
  readonly headers?: Readonly<Record<string, string>>;
};

/**
 * Uploads payloads to presigned URLs.
 *
 * @remarks
 * Implementations are expected to enforce secure usage (HTTPS-only)
 */
export interface S3Client {
  /**
   * Upload a JSON payload to a presigned URL.
   *
   * @param url - The presigned URL to upload to. Must use HTTPS.
   * @param payload - The value that will be serialized as JSON for the request body.
   * @param options - Optional upload configuration.
   *
   * @throws ApiError
   * Thrown when the URL is invalid, not HTTPS, the request times out, the network request fails,
   * or the server responds with a non-2xx status.
   */
  uploadJson(
    url: string,
    payload: unknown,
    options?: PresignedUrlUploadOptions,
  ): Promise<void>;
}

const MINIMUM_UPLOAD_TIMEOUT_MS = 10_000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 3 * 60_000;
const LOG_CONTEXT = 'S3Client' satisfies Context;

/**
 * Default {@link S3Client} implementation using `fetch`.
 *
 * @remarks
 * Enforces HTTPS-only presigned URLs and uploads JSON via `PUT`.
 */
@injectable()
export class S3ClientImpl implements S3Client {
  constructor(@inject(TYPES.Logger) private readonly logger: Logger) {}

  async uploadJson(
    url: string,
    payload: unknown,
    options: PresignedUrlUploadOptions = {},
  ): Promise<void> {
    const { timeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS, headers } = options;
    const effectiveTimeoutMs = Math.max(timeoutMs, MINIMUM_UPLOAD_TIMEOUT_MS);
    if (effectiveTimeoutMs !== timeoutMs) {
      this.logger.debug({
        context: LOG_CONTEXT,
        msg: `Provided timeoutMs (${timeoutMs}ms) was below the minimum (${MINIMUM_UPLOAD_TIMEOUT_MS}ms).`,
      });
    }
    const protocol = this.getUrlProtocolOrThrow(url);
    if (protocol !== 'https:') {
      throw new ApiError(
        'Presigned URL must use HTTPS for secure transmission.',
      );
    }
    const controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : undefined;
    const timeout = controller
      ? setTimeout(() => controller.abort(), effectiveTimeoutMs)
      : undefined;
    try {
      const method = 'PUT';
      const res = await fetch(url, {
        method,
        headers: this.buildJsonUploadHeaders(headers),
        body: JSON.stringify(payload),
        signal: controller?.signal,
      });
      if (!res.ok) {
        const bodyText = await this.tryReadText(res);
        const requestId = res.headers.get('x-amz-request-id') ?? undefined;
        const id2 = res.headers.get('x-amz-id-2') ?? undefined;
        const details = [
          `status=${res.status} ${res.statusText}`,
          requestId !== undefined && `x-amz-request-id=${requestId}`,
          id2 !== undefined && `x-amz-id-2=${id2}`,
          bodyText.length > 0 && `body=${bodyText.slice(0, 500)}`,
        ]
          .filter(Boolean)
          .join(' | ');
        throw new ApiError(`Upload to presigned URL failed: ${details}`);
      }
      this.logger.debug({
        context: LOG_CONTEXT,
        msg: 'Upload to presigned URL succeeded.',
      });
    } catch (err: unknown) {
      const errorShouldBeReWrapped = !(err instanceof ApiError);
      if (!errorShouldBeReWrapped) {
        throw err;
      }
      if (!(err instanceof Error)) {
        throw new ApiError(`Upload to presigned URL failed: ${String(err)}`);
      }
      if (err.name === 'AbortError') {
        throw new ApiError(
          `Upload to presigned URL timed out after ${effectiveTimeoutMs}ms.`,
        );
      }
      throw new ApiError(`Upload to presigned URL failed: ${err.message}`);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  private buildJsonUploadHeaders(
    headersParameter?: Readonly<Record<string, string>>,
  ): Headers {
    const jsonContentType = 'application/json';
    const headers = new Headers(headersParameter ?? {});
    const providedContentType = headers.get('content-type');
    if (
      isNotBlank(providedContentType) &&
      providedContentType !== jsonContentType
    ) {
      this.logger.debug({
        context: LOG_CONTEXT,
        msg: `content-type header cannot be overloaded (${providedContentType}).`,
      });
    }
    if (!headers.has('x-amz-server-side-encryption')) {
      headers.set('x-amz-server-side-encryption', 'AES256');
    }
    headers.set('content-type', jsonContentType);
    return headers;
  }

  private getUrlProtocolOrThrow(url: string): string {
    try {
      return new URL(url).protocol;
    } catch {
      throw new ApiError('Invalid presigned URL.');
    }
  }

  private async tryReadText(res: Response): Promise<string> {
    try {
      return await res.text();
    } catch {
      return '';
    }
  }
}
